/**
 * Submenu row for popover menus.
 *
 * Renders a parent menu row with a trailing `▶` affordance; hovering the row
 * (or pressing `ArrowRight` / `Enter` while it's the popover's active item)
 * opens a nested portal-positioned popover anchored to this row's right edge.
 * Leaving both the row and the submenu closes the submenu after a short
 * grace period so diagonal travel between them doesn't flicker.
 *
 * Built on `Popover` so nested submenus inherit all of its behavior:
 * click-outside dismiss, Escape, z-stacking, keyboard nav over
 * `data-popover-item` children, viewport clamping.
 */

import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Popover } from "../Popover.tsx";
import { COLOR_TEXT_DIM, FONT_SIZE_BODY } from "../tokens.ts";
import { MenuButtonRow } from "./MenuPrimitives.tsx";

// Delay closing after the pointer leaves both the row and the submenu.
// Short enough to feel responsive; long enough to allow diagonal travel.
const CLOSE_DELAY_MS = 150;

export function SubmenuRow({
  label,
  indicator,
  disabled,
  children,
}: {
  label: string;
  /** Optional right-aligned secondary text (before the ▶). */
  indicator?: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  const clearClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearClose();
    closeTimerRef.current = window.setTimeout(
      () => setOpen(false),
      CLOSE_DELAY_MS,
    );
  }, [clearClose]);

  useEffect(() => () => clearClose(), [clearClose]);

  // ArrowRight / Enter on this row (when it's the active popover item) opens
  // the submenu; ArrowLeft closes it.  The parent popover already dispatches
  // Enter as a .click() on the active element, so we listen on the row itself
  // for the arrow keys.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        clearClose();
        setOpen(true);
      } else if (e.key === "ArrowLeft" && open) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      }
    },
    [disabled, open, clearClose],
  );

  return (
    <>
      <MenuButtonRow
        ref={btnRef}
        // Intentionally not `data-popover-item` — that attribute opts the row
        // into Popover's first-item-highlight + keyboard-nav semantics, which
        // would permanently mark the submenu row as "active" inside an
        // otherwise hover-driven hamburger menu.  The row is fully
        // operable via click / pointer-enter / ArrowRight instead.
        onKeyDown={handleKeyDown}
        onClick={(e) => {
          if (disabled) return;
          e.preventDefault();
          clearClose();
          setOpen(true);
        }}
        onPointerEnter={() => {
          if (disabled) return;
          clearClose();
          setOpen(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType !== "touch") scheduleClose();
        }}
        disabled={disabled}
      >
        <span style={{ flex: 1 }}>{label}</span>
        {indicator && (
          <span
            style={{
              fontSize: FONT_SIZE_BODY - 1,
              color: COLOR_TEXT_DIM,
              marginLeft: 16,
            }}
          >
            {indicator}
          </span>
        )}
        <ChevronRight
          size={13}
          style={{ marginLeft: 8, color: COLOR_TEXT_DIM }}
        />
      </MenuButtonRow>

      <Popover
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        gap={2}
        placement="right"
      >
        <div
          onPointerEnter={clearClose}
          onPointerLeave={(e) => {
            if (e.pointerType !== "touch") scheduleClose();
          }}
          style={{ display: "flex", flexDirection: "column", minWidth: 180 }}
        >
          {children}
        </div>
      </Popover>
    </>
  );
}
